import { defineMessages } from 'react-intl';
import { DateTime } from 'luxon';
import { CollectionTicketClaimRevokeStrategyDTO } from '@/api/trafikportalen/models/collectionTicketClaimRevokeStrategyDTO';
import {
  hasDepartmentBearerManagerAccess,
  hasDepartmentWriteAccess,
} from './departmentPermissions';
import { getPeriodStatus } from './periodStatus';
import type {
  CollectionTicketClaimDTO,
  IssuedTicketBobcatDataDTO,
  IssuedTicketFindResultDTO,
} from '@/api/trafikportalen';

export const periodTicketCancelMessages = defineMessages({
  reason: {
    defaultMessage:
      'Biljetten kommer från en period och kan inte avslutas manuellt. Inaktivera resenären om biljetter ska bort direkt, annars ändra perioden.',
  },
});

export const ticketComesFromPeriod = (ticket: {
  collection_ticket_claim_id?: string;
}): boolean => Boolean(ticket.collection_ticket_claim_id);

type PeriodCancelClaim = Pick<
  CollectionTicketClaimDTO,
  'valid_from' | 'valid_to' | 'revoke_strategy'
>;

export const finishedPeriodAllowsManualCancel = (
  claim: PeriodCancelClaim,
): boolean =>
  getPeriodStatus(claim) === 'past' &&
  claim.revoke_strategy === CollectionTicketClaimRevokeStrategyDTO.none;

export const periodBlocksManualTicketCancel = (
  ticket: { collection_ticket_claim_id?: string },
  claim?: PeriodCancelClaim,
): boolean => {
  if (!ticketComesFromPeriod(ticket)) return false;
  if (!claim) return true;
  return !finishedPeriodAllowsManualCancel(claim);
};

export const showsActivationsLeft = <
  T extends {
    activations_total?: number;
    activations_left?: number;
  },
>(
  counts: T,
): counts is T & { activations_total: number; activations_left: number } =>
  counts.activations_left !== undefined && (counts.activations_total ?? 0) > 1;

export const ongoingActivationEnd = (
  bobcatData?: Pick<IssuedTicketBobcatDataDTO, 'current_validity_end'>,
  now: DateTime = DateTime.now(),
): string | undefined => {
  const end = bobcatData?.current_validity_end;
  if (!end) return undefined;
  return DateTime.fromISO(end) > now ? end : undefined;
};

export const ticketStatusDeadline = (
  ticket: { state: string; expires_at?: string },
  bobcatData?: Pick<
    IssuedTicketBobcatDataDTO,
    'current_validity_end' | 'latest_start_of_validity' | 'activations_total'
  >,
): string | undefined => {
  const isMultiUse = (bobcatData?.activations_total ?? 0) > 1;

  if (
    ticket.state === 'active' &&
    !isMultiUse &&
    bobcatData?.current_validity_end
  ) {
    return bobcatData.current_validity_end;
  }

  if (ticket.state === 'inactive' && bobcatData?.latest_start_of_validity) {
    return bobcatData.latest_start_of_validity;
  }

  return ticket.expires_at;
};

/** The status a ticket is shown and filtered as; a ticket being revoked counts as revoked. */
export const displayedTicketState = (state: string) =>
  state === 'marked_for_deletion' ? 'revoked' : state;

const ticketStateAllowsChanges = (ticket: IssuedTicketFindResultDTO) => {
  if (ticket.state !== 'active' && ticket.state !== 'inactive') {
    return false;
  }

  if (
    ticket.tags.some(
      (tag) =>
        (tag.startsWith('pc_') && tag.endsWith('single')) || tag === 'single',
    )
  ) {
    return ticket.state !== 'active';
  }
  return ticket.state === 'active' || ticket.state === 'inactive';
};

export const canCancelTicket = (ticket: IssuedTicketFindResultDTO) =>
  hasDepartmentWriteAccess(ticket.department_id) &&
  ticketStateAllowsChanges(ticket);

export const canChangeTicketBearer = (ticket: IssuedTicketFindResultDTO) =>
  hasDepartmentBearerManagerAccess(ticket.department_id) &&
  ticketStateAllowsChanges(ticket);
